import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { TableRow } from '@material-ui/core';
import { InventoryReportSnapshotItemRow } from './types';
import MemoizedTableRow, { RowHandlers } from './MemoizedTableRow';

const ROW_HEIGHT = 40;
const OVERSCAN = 5;

export interface VirtualizerApi {
    scrollToIndex: (index: number, options?: { align?: 'start' | 'center' | 'end' }) => void;
}

export interface VirtualTableBodyProps {
    rows: InventoryReportSnapshotItemRow[];
    editable: boolean;
    unsavedChanges: Record<string, { originalValue: number; newValue: number }>;
    handlers: RowHandlers;
    errorIconClassName?: string;
    focusRowId?: string | null;
    savingRowId?: string | null;
    parentRef: React.RefObject<HTMLDivElement | null>;
    virtualizerRef?: React.MutableRefObject<VirtualizerApi | null>;
}

const VirtualTableBody: React.FC<VirtualTableBodyProps> = ({
    rows,
    editable,
    unsavedChanges,
    handlers,
    errorIconClassName,
    focusRowId,
    savingRowId,
    parentRef,
    virtualizerRef,
}) => {
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: OVERSCAN,
        getItemKey: (index) => rows[index]?.id ?? index,
    });

    if (virtualizerRef) {
        virtualizerRef.current = {
            scrollToIndex: (index: number, options?: { align?: 'start' | 'center' | 'end' }) => {
                rowVirtualizer.scrollToIndex(index, options);
            },
        };
    }

    const virtualRows = rowVirtualizer.getVirtualItems();

    return (
        <div
            style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                minWidth: 'max-content',
                position: 'relative',
            }}
        >
            {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                if (!row) return null;
                const nextRow = rows[virtualRow.index + 1];
                const isLastInProductGroup = !nextRow || nextRow.productUuid !== row.productUuid;
                const borderBottom = isLastInProductGroup
                    ? '2px solid #9e9e9e'
                    : '1px solid #e0e0e0';
                const unsavedChange = row.id ? unsavedChanges[row.id] : undefined;
                return (
                    <TableRow
                        key={row.id}
                        component="div"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: `${virtualRow.size}px`,
                            transform: `translateY(${virtualRow.start}px)`,
                            display: 'flex',
                            margin: 0,
                            padding: 0,
                            borderBottom,
                            boxSizing: 'border-box',
                        }}
                    >
                        <MemoizedTableRow
                            row={row}
                            editable={editable}
                            unsavedChange={
                                unsavedChange
                                    ? { originalValue: unsavedChange.originalValue, newValue: unsavedChange.newValue }
                                    : null
                            }
                            handlers={handlers}
                            errorIconClassName={errorIconClassName}
                            focusRowId={focusRowId}
                            savingRowId={savingRowId}
                        />
                    </TableRow>
                );
            })}
        </div>
    );
};

export default React.memo(VirtualTableBody);
